import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AbilityBuilder, PureAbility, createMongoAbility } from '@casl/ability';
import * as store from '@daas-consulting/core-frontend-store';
import {
  congrats,
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import {
  AuthenticationService,
  AuthenticationServiceId,
} from '@daas-consulting/security/authentication/frontend/data_contracts';
import * as models from '@daas-consulting/security/authentication/frontend/models';
import { Navigate } from '@ngxs/router-plugin';
import { Action, State, StateContext, ofActionDispatched } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Observable, catchError, take, tap } from 'rxjs';
import * as actions from './authentication.actions';
import * as consts from './authentication.const';
import {
  AuthenticationAuthenticateForm,
  AuthenticationStateModel,
  AuthenticationTurnOnForm,
  NullableType,
} from './authentication.state-model';

export const defaults: AuthenticationStateModel = {
  userId: '',
  sessionId: '',
  email: '',
  name: '',
  picture: '',
  permissions: ['!manage:all'],
  accessToken: '',
  tokenType: '',
  expiresIn: 0,
  requestCode: null,
  qrCode: '',
  isAuthenticated: false,
  is2faFlowEnabled: false,
  turnOnForm: store.getFormModelDefault<AuthenticationTurnOnForm>({
    model: { code1: '', code2: '' },
  }),
  validateForm: store.getFormModelDefault<AuthenticationAuthenticateForm>({
    model: { code: '' },
  }),
  accessTokenDecoded: null,
};

const defaultPicture =
  'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png';

@State<AuthenticationStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class AuthenticationState {
  readonly #service: AuthenticationService;
  readonly #eventHelper: store.EventHelper;
  readonly #jwtService: JwtHelperService;
  readonly #ability: PureAbility;

  constructor(
    @Inject(AuthenticationServiceId) service: AuthenticationService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper,
    @Inject(JwtHelperService) jwtService: JwtHelperService,
    @Inject(PureAbility) ability: PureAbility
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this.#jwtService = jwtService;
    this.#ability = ability;
    this._connectEvents();
  }

  @Action(actions.Login)
  login(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.Login>
  ): void {
    const code = action.payload;
    const isAuthenticated = this._isAuthenticated(ctx);
    if (this._isValidCode(code)) {
      this._resolveIsNotAuthenticated(ctx, code);
      return;
    }

    if (isAuthenticated) {
      this._resolveIsAuthenticated(ctx);
    } else {
      this._resolveIsNotAuthenticated(ctx, code);
    }
  }

  @Action(actions.Logout)
  logout(ctx: StateContext<AuthenticationStateModel>): void {
    this._logout(ctx);
    this._resolveGotoLogin(ctx, {});
  }

  @Action(actions.LoginWithCode)
  loginWithCode(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.LoginWithCode>
  ): void {
    if (this._isValidCode(action.payload)) {
      ctx.setState(
        patch({
          requestCode: action.payload,
        })
      );
    }
  }

  @Action(actions.LoginWithCodeRequest)
  loginWithCodeRequest(ctx: StateContext<AuthenticationStateModel>): void {
    const state = ctx.getState();
    if (this._isValidCode(state.requestCode)) {
      this.#service
        .loginWithCode$(state.requestCode as string)
        .pipe(
          take(1),
          tap((data: models.Token) => {
            const action = new actions.LoginWithCodeSuccessful(data);
            ctx.dispatch(action);
          }),
          catchError((error) => {
            const action = new actions.LoginWithCodeFailure(error as Error);
            ctx.dispatch(action);
            return store.handleError(error);
          })
        )
        .subscribe();
    }
  }

  @Action(actions.LoginWithCodeSuccessful)
  loginWithCodeSuccessful(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.LoginWithCodeSuccessful>
  ): void {
    const state = ctx.getState();
    const picture = action?.payload?.picture || defaultPicture;
    const jwtAccessToken = action.payload?.accessToken;
    const accessTokenDecoded = this._getDecodedAccessToken(
      jwtAccessToken as string
    );
    ctx.setState(
      patch({
        ...action.payload,
        picture,
        accessTokenDecoded,
        sessionId: state.requestCode as string,
      })
    );
    const isAuthenticated = this._isAuthenticated(ctx);
    ctx.setState(
      patch({
        isAuthenticated,
      })
    );
    const token = action.payload as models.Token;
    if (models.is2faFlowEnabled(token)) {
      this._resolve2faFlow(ctx, token);
    } else {
      this._resolveCommonFlow(ctx, token);
    }
  }

  @Action(actions.LoginWithCodeFailure)
  loginWithCodeFailure(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.LoginWithCodeFailure>
  ): Observable<void> {
    this._resolveGotoLogin(ctx, {
      [models.Errors.internalError]: true,
      message: action.payload as Error,
    });
    const showErrorAction = new errorMessage.actions.ShowError(
      action.payload as Error
    );
    return ctx.dispatch(showErrorAction);
  }

  @Action(actions.GenerateRequest)
  generateRequest(ctx: StateContext<AuthenticationStateModel>): void {
    const state = ctx.getState();
    const userId = state.userId as string;
    this.#service
      .generateQrCode$(userId)
      .pipe(
        take(1),
        tap((data: string) => {
          const action = new actions.GenerateSuccessful(data);
          ctx.dispatch(action);
        }),
        catchError((error) => {
          const action = new actions.GenerateFailure(error as Error);
          ctx.dispatch(action);
          return store.handleError(error);
        })
      )
      .subscribe();
  }

  @Action(actions.GenerateSuccessful)
  generateSuccessful(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.GenerateSuccessful>
  ): void {
    const qrCode = <string>action.payload;
    ctx.setState(
      patch({
        qrCode,
      })
    );
  }

  @Action(actions.GenerateFailure)
  generateFailure(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.GenerateFailure>
  ): Observable<void> {
    const newAction = new errorMessage.actions.ShowError(
      action.payload as Error
    );
    return ctx.dispatch(newAction);
  }

  @Action(actions.TurnOn)
  turnOn(ctx: StateContext<AuthenticationStateModel>): void {
    ctx.setState(
      patch({
        qrCode: '',
      })
    );
  }

  @Action(actions.TurnOnRequest)
  turnOnRequest(ctx: StateContext<AuthenticationStateModel>): void {
    const state = ctx.getState();
    const userId = state.userId as string;
    const code1 = state.turnOnForm.model?.code1 as string;
    const code2 = state.turnOnForm.model?.code2 as string;
    if (
      state.turnOnForm.status === 'VALID' &&
      this._isCodeValid(code1) &&
      this._isCodeValid(code2) &&
      code1 !== code2
    ) {
      this.#service
        .turnOn$(userId, code1, code2)
        .pipe(
          take(1),
          tap(() => {
            const action = new actions.TurnOnSuccessful();
            ctx.dispatch(action);
          }),
          catchError((error) => {
            const action = new actions.TurnOnFailure(error as Error);
            ctx.dispatch(action);
            return store.handleError(error);
          })
        )
        .subscribe();
    } else {
      const error = new Error('Datos invalidos');
      const action = new actions.TurnOnFailure(error);
      ctx.dispatch(action);
    }
  }

  @Action(actions.TurnOnSuccessful)
  turnOnSuccessful(ctx: StateContext<AuthenticationStateModel>): void {
    ctx.setState(
      patch({
        turnOnForm: store.getFormModelDefault<AuthenticationTurnOnForm>({
          model: { code1: '', code2: '' },
        }),
      })
    );
    this._resolve2faValidate(ctx);
  }

  @Action(actions.TurnOnFailure)
  turnOnFailure(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.TurnOnFailure>
  ): Observable<void> {
    const newAction = new errorMessage.actions.ShowError(
      action.payload as Error
    );
    return ctx.dispatch(newAction);
  }

  @Action(actions.Validate2faRequest)
  validate2faRequest(ctx: StateContext<AuthenticationStateModel>): void {
    const state = ctx.getState();
    const code = state.validateForm.model?.code as string;
    if (this._isCodeValid(code)) {
      this.#service
        .validate2fa$(state.userId, code)
        .pipe(
          take(1),
          tap((data: models.Token) => {
            const action = new actions.Validate2faSuccessful(data);
            ctx.dispatch(action);
          }),
          catchError((error) => {
            const action = new actions.Validate2faFailure(error as Error);
            ctx.dispatch(action);
            return store.handleError(error);
          })
        )
        .subscribe();
    } else {
      const error = new Error('Datos invalidos');
      const action = new actions.Validate2faFailure(error);
      ctx.dispatch(action);
    }
  }

  @Action(actions.Validate2faSuccessful)
  validate2faSuccessful(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.Validate2faSuccessful>
  ): void {
    const jwtAccessToken = action.payload?.accessToken;
    const accessTokenDecoded = this._getDecodedAccessToken(
      jwtAccessToken as string
    );
    ctx.setState(
      patch({
        ...action.payload,
        accessTokenDecoded,
      })
    );
    const isAuthenticated = this._isAuthenticated(ctx);
    ctx.setState(
      patch({
        isAuthenticated,
      })
    );
    const token = action.payload as models.Token;
    if (models.is2faFlowEnabled(token)) {
      this._resolve2faFlow(ctx, token);
    } else {
      this._resolveCommonFlow(ctx, token);
    }
  }

  @Action(actions.Validate2faFailure)
  validate2faFailure(
    ctx: StateContext<AuthenticationStateModel>,
    action: InstanceType<typeof actions.Validate2faFailure>
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = (action.payload as any).error;
    if (error?.message === 'invalid credentials') {
      this._resolve2faValidate(ctx, {
        [models.Errors.invalidCode]: true,
        message: 'Código invalido',
      });
      const showErrorAction = new congrats.actions.ShowErrorMessage(
        'Código invalido'
      );
      ctx.dispatch(showErrorAction);
    } else {
      this._resolveGotoLogin(ctx, {
        [models.Errors.internalError]: true,
        message: error.message,
      });
      const showErrorAction = new errorMessage.actions.ShowError(
        action.payload as Error
      );
      ctx.dispatch(showErrorAction);
    }
  }

  @Action(actions.GotoGenerateQrCode)
  gotoGenerateQrCode(ctx: StateContext<AuthenticationStateModel>): void {
    this._resolveIsAuthenticated(ctx);
  }

  @Action(actions.GotoTurnOn)
  gotoTurnOn(ctx: StateContext<AuthenticationStateModel>): void {
    this._resolveIsAuthenticated(ctx, 'TurnOn');
  }

  @Action({ type: '@@UPDATE_STATE' })
  afterStorageHydrated(ctx: StateContext<AuthenticationStateModel>) {
    const state = ctx.getState();
    const isAuthenticated = this._isAuthenticated(ctx);
    ctx.setState(
      patch({
        turnOnForm: store.getFormModelDefault<AuthenticationTurnOnForm>({
          model: { code1: '', code2: '' },
        }),
        validateForm: store.getFormModelDefault<AuthenticationAuthenticateForm>(
          {
            model: { code: '' },
          }
        ),
        isAuthenticated,
      })
    );

    if (isAuthenticated) {
      this._updateAbility(state.permissions);
    }
  }

  private _resolveCommonFlow(
    ctx: StateContext<AuthenticationStateModel>,
    token: models.Token
  ): void {
    this._updateAbility(token.permissions);
    this._resolveGotoDefaultUrl(ctx);
  }

  private _resolveGotoDefaultUrl(
    ctx: StateContext<AuthenticationStateModel>
  ): void {
    // TODO: Extraer la url por defecto a una config.
    // Alternativa extraer la url por defecto del token
    // del usuario y que este definido por rol o por usuario.
    const action = new Navigate(['/patients/list']);
    ctx.dispatch(action);
  }

  private _resolveGotoLogin(
    ctx: StateContext<AuthenticationStateModel>,
    payload: Params
  ): void {
    const action = new Navigate(['/login'], payload);
    ctx.dispatch(action);
  }

  private _resolve2faFlow(
    ctx: StateContext<AuthenticationStateModel>,
    token: models.Token
  ): void {
    const state = ctx.getState();
    const is2faEnabled = models.is2faEnabled(
      state.accessTokenDecoded as models.AccessToken
    );
    const is2faValidated = models.is2faValidated(
      state.accessTokenDecoded as models.AccessToken
    );

    if (/* Verificar si el usuario tiene habilitado el 2fa */ is2faEnabled) {
      if (/* Verificar si el usuario ya esta autenticado */ is2faValidated) {
        // Redirecciona al usuario a la página predeterminada.
        this._resolveCommonFlow(ctx, token);
      } else {
        // Redirecciona al usuario a la página para cargar el 2fa.
        this._resolve2faValidate(ctx);
      }
    } else {
      // Redirecciona al usuario a la página para habilitar el 2fa.
      this._resolve2faRegister(ctx);
    }
  }

  private _getDecodedAccessToken(accessToken: string): models.AccessToken {
    const rv = this.#jwtService.decodeToken<models.AccessToken>(accessToken);

    // Si no se puede decodificar el token, se lanza un error.
    if (typeof rv !== 'object' || rv === null) {
      throw new Error('invalid accessToken');
    }

    return rv;
  }

  private _resolve2faValidate(
    ctx: StateContext<AuthenticationStateModel>,
    payload?: Params
  ): void {
    const action = new Navigate(['/security/2fa/validate'], payload);
    ctx.dispatch(action);
  }

  private _resolve2faRegister(
    ctx: StateContext<AuthenticationStateModel>
  ): void {
    const action = new Navigate(['/security/2fa/generate']);
    ctx.dispatch(action);
  }

  private _resolve2faTurnOn(ctx: StateContext<AuthenticationStateModel>): void {
    const action = new Navigate(['/security/2fa/turn-on']);
    ctx.dispatch(action);
  }

  private _connectEvents(): void {
    // Conecta los eventos.
    this._connectEventsLoginWithCode();
    this._connectEventsGenerate();
    this._connectEventsTurnOn();
    this._connectEventsValidate();
  }

  private _connectEventsLoginWithCode(): void {
    this.#eventHelper.connectEvents(
      actions.LoginWithCode,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.LoginWithCode,
      actions.LoginWithCodeRequest
    );
    this.#eventHelper.connectEvents(
      actions.LoginWithCodeSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.LoginWithCodeFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }

  private _connectEventsGenerate(): void {
    this.#eventHelper.connectEvents(
      actions.Generate,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(actions.Generate, actions.GenerateRequest);
    this.#eventHelper.connectEvents(
      actions.GenerateSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.GenerateFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }

  private _connectEventsTurnOn(): void {
    this.#eventHelper.connectEvents(
      actions.TurnOn,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(actions.TurnOn, actions.TurnOnRequest);
    this.#eventHelper.connectEvents(
      actions.TurnOnSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.TurnOnFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }

  private _connectEventsValidate(): void {
    this.#eventHelper.connectEvents(
      actions.Validate2fa,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.Validate2fa,
      actions.Validate2faRequest
    );
    this.#eventHelper.connectEvents(
      actions.Validate2faSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.Validate2faFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }

  private _updateAbility(permissions: string[]): void {
    const { can, cannot, rules } = new AbilityBuilder<PureAbility>(
      createMongoAbility
    );
    (permissions ?? []).forEach((permission) => {
      const match = /(?<not>!)?(?<action>[\w-]+):(?<permission>[\w\-.]+)/i.exec(
        permission
      );
      if (match) {
        const groups = match.groups;
        if (groups) {
          const not = groups['not'];
          const action = groups['action'];
          const permission = groups['permission'];
          if (not === '!') {
            cannot(action, permission);
          } else {
            can(action, permission);
          }
        }
      }
    });

    this.#ability.update(rules);
  }

  private _isValidCode(value: unknown): boolean {
    const rv = typeof value === 'string' && value.length > 0;
    return rv;
  }

  private _isTokenExpired(value: string): boolean {
    let rv = true;
    if (typeof value === 'string' && value.length > 0) {
      rv = this.#jwtService.isTokenExpired(value, 60 * 30);
    }

    return rv;
  }

  private _logout(ctx: StateContext<AuthenticationStateModel>): void {
    const state = ctx.getState();
    ctx.setState(
      patch({
        ...defaults,
        email: state.email,
        name: state.name,
        picture: state.picture,
        userId: state.userId,
        isAuthenticated: false,
        is2faFlowEnabled: false,
      })
    );
  }

  private _isAuthenticated(
    ctx: StateContext<AuthenticationStateModel>
  ): boolean {
    const state = ctx.getState();
    const jwtAccessToken = state.accessToken;
    const isTokenExpired = this._isTokenExpired(jwtAccessToken);
    const rv = !isTokenExpired;
    return rv;
  }

  private _resolveIsAuthenticated(
    ctx: StateContext<AuthenticationStateModel>,
    option: 'GenerateQRCode' | 'TurnOn' = 'GenerateQRCode'
  ): void {
    const state = ctx.getState();
    if (state.is2faFlowEnabled) {
      const is2faValidated = models.is2faValidated(
        state.accessTokenDecoded as models.AccessToken
      );
      if (is2faValidated) {
        this._resolveGotoDefaultUrl(ctx);
      } else {
        const is2faEnabled = models.is2faEnabled(
          state.accessTokenDecoded as models.AccessToken
        );
        if (is2faEnabled) {
          this._resolve2faValidate(ctx);
        } else {
          if (option === 'GenerateQRCode') {
            this._resolve2faRegister(ctx);
          } else if (option === 'TurnOn') {
            this._resolve2faTurnOn(ctx);
          } else {
            throw new Error(`unrecognized option: ${option}`);
          }
        }
      }
    } else {
      // Sin 2fa
      this._resolveGotoDefaultUrl(ctx);
    }
  }

  private _resolveIsNotAuthenticated(
    ctx: StateContext<AuthenticationStateModel>,
    code: NullableType<string>
  ): void {
    const state = ctx.getState();
    if (this._isValidCode(code)) {
      const loginWithCodeAction = new actions.LoginWithCode(code);
      ctx.dispatch(loginWithCodeAction);
    } else if (this._isTokenExpired(state.accessToken)) {
      this._logout(ctx);
      this._resolveGotoLogin(ctx, {
        [models.Errors.tokenExpired]: true,
      });
    }
  }

  private _isCodeValid(code: NullableType<string>): boolean {
    const rv =
      typeof code === 'string' && code.length > 0 && /\d{6}/.test(code);
    return rv;
  }
}
